// ============================================================
// Session gate — wraps the authenticated app shell.
//   loading          → status screen ("正在恢复会话")
//   error            → status screen with Retry / re-login
//   unauthenticated  → redirect to /login?redirect=<current>
//   authenticated    → render children
//
// Reacts to token changes via useSyncExternalStore, so an expired
// token (cleared by withAuth on 401) or a cross-tab logout drops the
// user back to /login automatically.
// ============================================================

import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { useAuthActions } from '../hooks/useAuthActions';
import {
  clearExplicitLogout,
  isExplicitLogout,
  resolveAuthRedirect,
} from '../utils/redirect';
import { AuthStatusScreen } from './AuthLayout';

export function AuthGuard() {
  const { t } = useTranslation(['auth', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { logout, isLoggingOut } = useAuthActions();
  // 未认证跳转只执行一次。不能用 <Navigate> 声明式组件：路由过渡期旧组件树
  // 会带着「新 location」重渲染，而 <Navigate> 每次渲染（props 引用变化）
  // 都会重放导航 —— 第二次导航用新 URL 重算 redirect，要么把 ?redirect=
  // 弄丢，要么自我包裹成 /login?redirect=/login?redirect=… 的循环。
  const redirectedToLogin = useRef(false);

  useEffect(() => {
    if (auth.isLoading || auth.isError) return;
    // 已恢复登录：显式登出标记作废（见 utils/redirect.ts），
    // 避免旧标记误伤后续真实的会话过期跳转。
    if (auth.isAuthenticated) {
      clearExplicitLogout();
      return;
    }
    if (redirectedToLogin.current) return;
    redirectedToLogin.current = true;
    // 显式登出引发的未认证跳转不携带 ?redirect=（登出即彻底离开，
    // 重新登录回到默认首页）；会话过期/401 仍保留回跳目标。
    const redirect = isExplicitLogout()
      ? ''
      : resolveAuthRedirect(location.pathname + location.searchStr, '');
    void navigate(
      redirect
        ? { to: '/login', search: { redirect }, replace: true }
        : { to: '/login', replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.isLoading,
    auth.isError,
    auth.isAuthenticated,
    location.pathname,
    location.searchStr,
  ]);

  // isLoading 基于 hasToken：token 存在且资料加载中 → 应用内蒙层
  // （覆盖式 loading，而不是整页换成登录页外观的恢复屏，刷新不跳变）。
  // isAuthenticated 已由 useAuth 基于 hasToken + 账户快照判定，
  // 因此刷新不会因为资料未返回而闪登录页。
  // 全局 app loader 由 AuthLoadingController 控制，守卫只负责暂停路由内容。
  if (auth.isLoading) return null;

  if (auth.isError) {
    return (
      <AuthStatusScreen
        title={t('session.unavailableTitle', { ns: 'auth' })}
        description={t('session.unavailableDescription', { ns: 'auth' })}
        action={
          <div className="flex flex-wrap justify-center gap-2.5">
            <Button onClick={() => void auth.retry()}>
              {t('actions.retry', { ns: 'common' })}
            </Button>
            <Button
              variant="outline"
              disabled={isLoggingOut}
              onClick={() => void logout()}
            >
              {t('actions.relogin', { ns: 'common' })}
            </Button>
          </div>
        }
      />
    );
  }

  // 未认证：effect 已发起跳转，此帧不渲染内容。
  if (!auth.isAuthenticated) return null;

  return <Outlet />;
}
